// @ts-nocheck
import React from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { usePermissions } from '@/hooks/usePermissions'
import { Card, CardContent } from '@/components/ui/card'
import { Shield } from 'lucide-react'

/**
 * Role hierarchy:
 *   SUPER_ADMIN > ADMIN > ADVISOR > CLIENT > VIEWER
 *
 * Allowed role values (lowercase, as returned by the response transformer):
 *   'super_admin' | 'admin' | 'advisor' | 'client' | 'viewer'
 */

interface RequireRoleProps {
  /** Roles that are allowed to access the wrapped content */
  allowed: string[]
  /** Where to redirect if denied (default: show inline "Access Denied") */
  redirectTo?: string
  children: React.ReactNode
}

export default function RequireRole({ allowed, redirectTo, children }: RequireRoleProps) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  const userRole = (user.role || '').toLowerCase()

  if (!allowed.includes(userRole)) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />
    }

    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <Shield className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
            <p className="text-sm text-slate-500">
              You don't have permission to access this page. Please contact your administrator if you believe this is an error.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return <>{children}</>
}

/** Convenience: only super_admin */
export function RequireSuperAdmin({ children }: { children: React.ReactNode }) {
  return <RequireRole allowed={['super_admin']}>{children}</RequireRole>
}

/** Convenience: super_admin + admin */
export function RequireAdmin({ children }: { children: React.ReactNode }) {
  return <RequireRole allowed={['super_admin', 'admin']}>{children}</RequireRole>
}

/** Convenience: super_admin + admin + advisor (all staff) */
export function RequireStaff({ children }: { children: React.ReactNode }) {
  return <RequireRole allowed={['super_admin', 'admin', 'advisor']}>{children}</RequireRole>
}

/**
 * Permission-based route guard.
 * Checks the user's effective permissions (Role → Teams → User).
 * Admins always pass. Non-admin users need the specified permission.
 */
interface RequirePermissionProps {
  module: string
  action: string
  redirectTo?: string
  children: React.ReactNode
}

export function RequirePermission({ module, action, redirectTo, children }: RequirePermissionProps) {
  const { user, loading } = useAuth()
  const { can } = usePermissions()

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!user) return <Navigate to="/login" replace />

  if (!can(module, action)) {
    if (redirectTo) return <Navigate to={redirectTo} replace />
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <Shield className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
            <p className="text-sm text-slate-500">
              You don't have permission to access this page. Please contact your administrator if you believe this is an error.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return <>{children}</>
}

interface RequireAnyPermissionProps {
  checks: [string, string][]
  redirectTo?: string
  children: React.ReactNode
}

export function RequireAnyPermission({ checks, redirectTo, children }: RequireAnyPermissionProps) {
  const { user, loading } = useAuth()
  const { canAny } = usePermissions()

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!user) return <Navigate to="/login" replace />

  if (!canAny(...checks)) {
    if (redirectTo) return <Navigate to={redirectTo} replace />
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <Shield className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
            <p className="text-sm text-slate-500">
              You don't have permission to access this page. Please contact your administrator if you believe this is an error.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return <>{children}</>
}
